<template>
	<div
		ref="rootRef"
		:class="bemm('', { 'is-open': isOpen, 'is-disabled': disabled })"
	>
		<slot
			name="trigger"
			:is-open="isOpen"
			:toggle="toggleMenu"
			:disabled="disabled"
		>
			<Button
				:icon="triggerIcon"
				:status="status"
				:color="color"
				:variant="variant"
				:size="size"
				:disabled="disabled"
				@click="toggleMenu"
			>
				<span :class="bemm('label')">{{ label }}</span>
				<span :class="bemm('caret')" aria-hidden="true">
					<Icon :name="Icons.CHEVRON_DOWN" />
				</span>
			</Button>
		</slot>
	</div>
</template>

<script lang="ts" setup>
import {
	PropType,
	computed,
	defineComponent,
	markRaw,
	onBeforeUnmount,
	ref,
	useSlots,
	watch,
} from 'vue';
import { useBemm } from 'bemm';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Color, Size, Status } from '@arritech/shared/types';
import { Icons } from '../Icon';
import {
	contextMenuService,
	createContextMenuInstanceId,
} from '../ContextMenu';
import type {
	DropdownItem,
	DropdownOption,
	DropdownAlign,
} from './Dropdown.model';

const props = defineProps({
	items: {
		type: Array as PropType<DropdownOption[]>,
		default: () => [],
	},
	label: {
		type: String as PropType<string>,
		default: 'Actions',
	},
	icon: {
		type: String as PropType<DropdownItem['icon']>,
		default: null,
	},
	color: {
		type: String as PropType<Color>,
		default: Color.SECONDARY,
	},
	status: {
		type: String as PropType<Status | null>,
		default: null,
	},
	variant: {
		type: String as PropType<'default' | 'ghost' | 'outline'>,
		default: 'default',
	},
	size: {
		type: String as PropType<Size>,
		default: Size.MEDIUM,
	},
	align: {
		type: String as PropType<DropdownAlign>,
		default: 'left',
	},
	disabled: {
		type: Boolean as PropType<boolean>,
		default: false,
	},
	closeOnSelect: {
		type: Boolean as PropType<boolean>,
		default: true,
	},
	openUp: {
		type: Boolean as PropType<boolean>,
		default: false,
	},
});

const emit = defineEmits(['select', 'change']);

const bemm = useBemm('ar-dropdown', {
	return: 'string',
	includeBaseClass: true,
});
const slots = useSlots();
const rootRef = ref<HTMLElement | null>(null);
const menuInstanceId = createContextMenuInstanceId('dropdown');
const isOpen = computed(
	() => contextMenuService.menu.value?.id === menuInstanceId
);
const VIEWPORT_PADDING = 8;

const triggerIcon = computed(() => props.icon || null);

const resolveMenuPosition = () => {
	const root = rootRef.value;
	if (!root) {
		return null;
	}

	const rect = root.getBoundingClientRect();

	return {
		top: rect.top,
		right: rect.right,
		bottom: rect.bottom,
		left: rect.left,
		minWidth: Math.max(180, Math.round(rect.width)),
		anchor: root,
	};
};

const estimateMenuHeight = () => {
	if (slots.menu) {
		return 320;
	}

	const itemCount = props.items.filter(
		(item) => item.type !== 'divider'
	).length;
	const dividerCount = props.items.length - itemCount;
	const estimatedItemHeight = itemCount * 34;
	const estimatedDividerHeight = dividerCount * 9;
	const estimatedPadding = 18;

	return Math.max(
		80,
		estimatedItemHeight + estimatedDividerHeight + estimatedPadding
	);
};

const resolveViewportSafePlacement = (position: {
	top: number;
	right: number;
	bottom: number;
	left: number;
	minWidth: number;
}) => {
	let align = props.align;
	let openUp = props.openUp;

	if (typeof window === 'undefined') {
		return { align, openUp };
	}

	const menuWidth = position.minWidth;
	const menuHeight = estimateMenuHeight();

	if (align === 'left') {
		const overflowsRight =
			position.left + menuWidth > window.innerWidth - VIEWPORT_PADDING;
		const hasRoomOnLeft = position.right - menuWidth >= VIEWPORT_PADDING;
		if (overflowsRight && hasRoomOnLeft) {
			align = 'right';
		}
	} else {
		const overflowsLeft = position.right - menuWidth < VIEWPORT_PADDING;
		const hasRoomOnRight =
			position.left + menuWidth <= window.innerWidth - VIEWPORT_PADDING;
		if (overflowsLeft && hasRoomOnRight) {
			align = 'left';
		}
	}

	const overflowsBottom =
		position.bottom + VIEWPORT_PADDING + menuHeight >
		window.innerHeight - VIEWPORT_PADDING;
	const hasRoomAbove = position.top - VIEWPORT_PADDING - menuHeight >= 0;
	const overflowsTop = position.top - VIEWPORT_PADDING - menuHeight < 0;
	const hasRoomBelow =
		position.bottom + VIEWPORT_PADDING + menuHeight <= window.innerHeight;

	if (!openUp && overflowsBottom && hasRoomAbove) {
		openUp = true;
	} else if (openUp && overflowsTop && hasRoomBelow) {
		openUp = false;
	}

	return { align, openUp };
};

const createMenuSlotComponent = () => {
	if (!slots.menu) {
		return null;
	}

	return markRaw(
		defineComponent({
			name: 'DropdownMenuSlotContent',
			setup() {
				return () =>
					slots.menu?.({
						isOpen: isOpen.value,
						closeMenu,
					});
			},
		})
	);
};

const openMenu = () => {
	if (props.disabled) {
		return;
	}

	const position = resolveMenuPosition();
	if (!position) {
		return;
	}
	const placement = resolveViewportSafePlacement(position);
	const x = placement.align === 'right' ? position.right : position.left;
	const y = placement.openUp ? position.top : position.bottom;

	contextMenuService.open({
		id: menuInstanceId,
		items: props.items,
		align: placement.align,
		position: 'fixed',
		openUp: placement.openUp,
		fullWidth: true,
		minWidth: position.minWidth,
		x,
		y,
		closeOnSelect: props.closeOnSelect,
		anchor: position.anchor,
		beforeComponent: createMenuSlotComponent(),
		onSelect: handleSelect,
	});
};

const toggleMenu = () => {
	if (isOpen.value) {
		closeMenu();
		return;
	}

	openMenu();
};

const closeMenu = () => {
	contextMenuService.close(menuInstanceId);
};

const handleSelect = (item: DropdownItem) => {
	if (item.disabled || item.locked || item.divider) {
		return;
	}
	emit('select', item);
};

watch(isOpen, (nextIsOpen) => {
	emit('change', nextIsOpen);
});

watch(
	() => props.disabled,
	(nextDisabled) => {
		if (!nextDisabled) {
			return;
		}

		closeMenu();
	}
);

watch(
	() => props.items,
	() => {
		if (!isOpen.value) {
			return;
		}

		openMenu();
	},
	{ deep: true }
);

watch(
	() => [props.align, props.openUp, props.closeOnSelect] as const,
	() => {
		if (!isOpen.value) {
			return;
		}

		openMenu();
	}
);

onBeforeUnmount(() => {
	closeMenu();
});
</script>

<style lang="scss">
.ar-dropdown {
	$b: &;
	position: relative;
	display: inline-flex;

	&__label {
		margin-right: var(--space-xs);
	}

	&--is-disabled {
		opacity: 0.6;
	}

	&__divider {
		height: 2px;
		border: none;
		background-color: color-mix(var(--color-foreground), transparent 95%);
		pointer-events: none;
		cursor: default;
	}

	&__caret {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		margin-left: var(--space-xs);
	}
}
</style>
